import { useCallback, useRef, useState } from 'react';
import type { FileChunk, DeliveryComplete } from '@/features/distributed-search/data/search.types';
import {
  deriveSessionKey,
  decryptFile,
  triggerDownload,
} from '../crypto/client-crypto';
import { getEphemeralKeystore } from '../crypto/ephemeralKeystore';

export interface FileDeliveryMeta {
  requestId: string;
  filename: string;
  sizeBytes: number;
}

export interface UseFileDeliveryOptions {
  /** Receives the decrypted file. Plaintext must not be persisted by the callback. */
  onComplete?: (blob: Blob, meta: FileDeliveryMeta) => void;
  /** Trigger a browser download after decrypt (default true). */
  autoDownload?: boolean;
}

export interface FileDeliveryState {
  status: 'idle' | 'receiving' | 'decrypting' | 'done' | 'error';
  progress: number; // 0-100
  errorMessage: string | null;
}

export function useFileDelivery(options: UseFileDeliveryOptions = {}) {
  const { onComplete, autoDownload = true } = options;
  const [state, setState] = useState<FileDeliveryState>({
    status: 'idle',
    progress: 0,
    errorMessage: null,
  });

  // DSEARCH-RULE-03: In-memory only (never in React state or localStorage)
  const privateKeyRef = useRef<CryptoKey | null>(null);
  const chunksRef = useRef<Uint8Array[]>([]);

  const receiveChunk = useCallback((chunk: FileChunk) => {
    const binary = atob(chunk.data);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    chunksRef.current.push(bytes);
    const progress = Math.min(
      100,
      Math.round(((chunk.chunkIndex + 1) / chunk.totalChunks) * 100),
    );
    setState(prev => ({
      ...prev,
      status: 'receiving',
      progress,
      errorMessage: null,
    }));
  }, []);

  const finalize = useCallback(
    async (
      completion: DeliveryComplete,
      ownerPublicKeyPem: string,
      filename: string,
    ) => {
      // Explicit key (setPrivateKey) wins; otherwise read the ephemeral keystore by requestId.
      const privateKey =
        privateKeyRef.current ??
        (await getEphemeralKeystore().load(completion.requestId));
      if (!privateKey) {
        setState(prev => ({
          ...prev,
          status: 'error',
          errorMessage: 'Private key không tồn tại',
        }));
        return;
      }

      setState(prev => ({
        ...prev,
        status: 'decrypting',
        progress: 100,
        errorMessage: null,
      }));

      try {
        const sessionKey = await deriveSessionKey(
          privateKey,
          ownerPublicKeyPem,
        );

        // Concatenate all chunks
        const totalLength = chunksRef.current.reduce(
          (acc, c) => acc + c.length,
          0,
        );
        const combinedCiphertext = new Uint8Array(totalLength);
        let offset = 0;
        for (const chunk of chunksRef.current) {
          combinedCiphertext.set(chunk, offset);
          offset += chunk.length;
        }

        // Extract 12-byte AES-GCM nonce and ciphertext payload
        const nonce = combinedCiphertext.slice(0, 12);
        const encryptedData = combinedCiphertext.slice(12);

        // DSEARCH-RULE-03: decrypt → trigger download immediately
        const plaintext = await decryptFile(encryptedData, nonce, sessionKey);
        if (autoDownload) triggerDownload(plaintext, filename);
        if (onComplete) {
          onComplete(new Blob([plaintext as unknown as BlobPart]), {
            requestId: completion.requestId,
            filename,
            sizeBytes: plaintext.length,
          });
        }
        await getEphemeralKeystore().remove(completion.requestId); // single use

        // Clear chunks immediately from memory
        chunksRef.current = [];
        setState(prev => ({
          ...prev,
          status: 'done',
          errorMessage: null,
        }));
      } catch (err) {
        setState(prev => ({
          ...prev,
          status: 'error',
          errorMessage: 'Không thể giải mã file. Khoá có thể không khớp.',
        }));
      }
    },
    [onComplete, autoDownload],
  );

  const setPrivateKey = useCallback((key: CryptoKey) => {
    privateKeyRef.current = key;
  }, []);

  const reset = useCallback(() => {
    privateKeyRef.current = null;
    chunksRef.current = [];
    setState({
      status: 'idle',
      progress: 0,
      errorMessage: null,
    });
  }, []);

  return {
    ...state,
    receiveChunk,
    finalize,
    setPrivateKey,
    reset,
    // Expose ref access for testing verification if needed
    _chunksRef: chunksRef,
    _privateKeyRef: privateKeyRef,
  };
}
